/**
 * =============================================================================
 * StudioPageSubmissions — Acompanhamento dos envios de uma página pública
 *
 * Mostra quantas visitas a página recebeu, quantos formulários foram enviados
 * e a lista dos envios com busca, paginação e a origem do visitante
 * (campanha/UTM e site de referência).
 *
 * Toda leitura passa por RPC (`studio_list_page_submissions`); não há acesso
 * direto a tabelas a partir do navegador.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-10 v1.0.0 - Versão inicial (Studio, páginas públicas Fase 4).
 * =============================================================================
 */
import React, { useMemo, useState } from 'react';
import { Eye, Inbox, Loader2, Search, UserRound } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InlineNotice } from '@/components/ui/inline-notice';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useStudioPageSubmissions } from '@/hooks/useStudioTemplates';
import type { StudioPageSubmission } from '@/lib/studio/api';

/** Quantidade de envios por página. */
const POR_PAGINA = 20;

interface Props {
  /** Modelo (página pública) em edição. */
  templateId: string;
}

/** Formata data/hora no padrão brasileiro. */
function formatarData(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/** Resume a origem do visitante em texto curto. */
function resumirOrigem(atribuicao: Record<string, unknown>): string {
  const origem = String(atribuicao?.utm_source ?? '').trim();
  const campanha = String(atribuicao?.utm_campaign ?? '').trim();
  const referencia = String(atribuicao?.referrer ?? '').trim();

  if (origem || campanha) return [origem, campanha].filter(Boolean).join(' · ');
  if (referencia) {
    try {
      return new URL(referencia).hostname;
    } catch {
      return referencia;
    }
  }
  return 'Acesso direto';
}

/** Mostra os campos preenchidos pelo visitante. */
function CamposEnviados({ envio }: { envio: StudioPageSubmission }) {
  const campos = useMemo(
    () =>
      Object.entries(envio.data_json ?? {}).filter(
        ([, valor]) => valor !== null && valor !== undefined && String(valor).trim() !== '',
      ),
    [envio.data_json],
  );

  if (!campos.length) {
    return <p className="text-xs text-muted-foreground">Sem campos preenchidos.</p>;
  }

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {campos.map(([chave, valor]) => (
        <span key={chave} className="text-xs">
          <span className="text-muted-foreground">{chave}: </span>
          <span className="font-medium">{String(valor)}</span>
        </span>
      ))}
    </div>
  );
}

export function StudioPageSubmissions({ templateId }: Props) {
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [pagina, setPagina] = useState(0);

  const { data, isLoading, isFetching } = useStudioPageSubmissions({
    templateId,
    search: buscaAplicada || null,
    limit: POR_PAGINA,
    offset: pagina * POR_PAGINA,
  });

  const itens = data?.items ?? [];
  const total = data?.total ?? 0;
  const visitas = data?.views ?? 0;
  const ultimaPagina = Math.max(0, Math.ceil(total / POR_PAGINA) - 1);

  /** Aplica a busca voltando para a primeira página. */
  function aplicarBusca(valor: string) {
    setBusca(valor);
    setPagina(0);
    setBuscaAplicada(valor.trim());
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-3">
      {/* --------------------------------------------------------- Resumo */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-1.5">
          <Eye className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Visitas</span>
          <span className="text-sm font-semibold">{visitas.toLocaleString('pt-BR')}</span>
        </div>
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-1.5">
          <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Envios</span>
          <span className="text-sm font-semibold">{total.toLocaleString('pt-BR')}</span>
        </div>

        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={busca}
            onChange={(e) => aplicarBusca(e.target.value)}
            placeholder="Buscar por nome, e-mail, telefone..."
            className="h-8 pl-8 text-xs"
            maxLength={120}
          />
        </div>
      </div>

      {/* --------------------------------------------------------- Listagem */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : itens.length === 0 ? (
        <InlineNotice variant="info">
          {buscaAplicada
            ? 'Nenhum envio encontrado para essa busca.'
            : 'Esta página ainda não recebeu envios de formulário.'}
        </InlineNotice>
      ) : (
        <ScrollArea className="min-h-0 flex-1 rounded-md border">
          <ul className="divide-y">
            {itens.map((envio) => (
              <li key={envio.id} className="space-y-1.5 px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">
                    {envio.contact_name ?? 'Visitante sem cadastro'}
                  </span>
                  {envio.contact_id && (
                    <Badge variant="secondary" className="h-5 gap-1 text-[10px]">
                      <UserRound className="h-3 w-3" />
                      Contato criado
                    </Badge>
                  )}
                  {envio.version_number != null && (
                    <Badge variant="outline" className="h-5 text-[10px]">
                      Versão {envio.version_number}
                    </Badge>
                  )}
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {formatarData(envio.created_at)}
                  </span>
                </div>

                <CamposEnviados envio={envio} />

                <p className="text-[11px] text-muted-foreground">
                  Origem: {resumirOrigem(envio.attribution_json ?? {})} · Formulário:{' '}
                  {envio.form_id}
                </p>
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}

      {/* --------------------------------------------------------- Paginação */}
      {total > POR_PAGINA && (
        <div className="flex items-center justify-end gap-2">
          <span className="text-[11px] text-muted-foreground">
            Página {pagina + 1} de {ultimaPagina + 1}
            {isFetching && ' · atualizando...'}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={pagina === 0 || isFetching}
            onClick={() => setPagina((p) => Math.max(0, p - 1))}
          >
            Anterior
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={pagina >= ultimaPagina || isFetching}
            onClick={() => setPagina((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}
